/* Liquid Sky: one slide per available product/color, plus the original hero. */
(() => {
  'use strict';
  const INTERVAL = 3000;
  const DESTINATION = '/collections/all-pg';
  const imageUrl = (source) => {
    if (!source) return '';
    const value = typeof source === 'string' ? source : source.src || source.url || '';
    try {
      const url = new URL(value.startsWith('//') ? 'https:' + value : value);
      if (url.protocol !== 'https:') return '';
      if (!(url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopify.com') || url.hostname === 'liquidskynyc.com')) return '';
      url.searchParams.set('width', '1200');
      return url.href;
    } catch { return ''; }
  };

  function interleave(groups) {
    const slides = [];
    const max = Math.max(0, ...groups.map((group) => group.length));
    for (let color = 0; color < max; color++) {
      for (const group of groups) if (group[color]) slides.push(group[color]);
    }
    return slides;
  }

  function buildSlides(products) {
    const groups = [];
    const seenProducts = new Set();
    for (const product of products) {
      if (!product || seenProducts.has(String(product.id))) continue;
      seenProducts.add(String(product.id));
      const colorIndex = (product.options || []).findIndex((option) => /^(colou?r)s?$/i.test(typeof option === 'string' ? option : option.name));
      const colors = new Map();
      for (const variant of product.variants || []) {
        if (!variant.available) continue;
        const color = colorIndex >= 0 ? String(variant['option' + (colorIndex + 1)] || (variant.options || [])[colorIndex] || '') : '';
        const associatedImage = variant.featured_image || (product.images || []).find((item) => (item.variant_ids || []).map(String).includes(String(variant.id)));
        const image = imageUrl(associatedImage || product.featured_image || (product.images || [])[0]);
        if (!image) continue;
        const existing = colors.get(color);
        if (existing && (existing.assignedImage || !associatedImage)) continue;
        colors.set(color, { productId: String(product.id), title: product.title, color, image, artwork: false, assignedImage: Boolean(associatedImage) });
      }
      if (colors.size) groups.push([...colors.values()]);
    }
    return groups.flat();
  }

  function normalizeSeed(seed) {
    const groups = new Map();
    const artwork = [];
    for (const slide of seed) {
      if (!imageUrl(slide.image)) continue;
      slide.image = imageUrl(slide.image);
      if (slide.artwork) { artwork.push(slide); continue; }
      const key = String(slide.productId);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(slide);
    }
    return artwork.slice(0, 1).concat([...groups.values()].flat());
  }

  const api = { INTERVAL, DESTINATION, imageUrl, buildSlides, normalizeSeed, interleave };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || !window.customElements) return;
  window.LiquidSkyCatalog = api;
  if (customElements.get('ls-product-carousel')) return;

  class LiquidSkyCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.slides = [];
      this.index = 0;
      this.activeLayer = 0;
      this.paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.images = [...this.querySelectorAll('[data-ls-image]')];
      this.imageLink = this.querySelector('[data-ls-link]');
      this.titleNode = this.querySelector('[data-ls-title]');
      this.colorNode = this.querySelector('[data-ls-color]');
      this.counter = this.querySelector('[data-ls-count]');
      this.caption = this.querySelector('[data-ls-caption]');
      this.pauseButton = this.querySelector('[data-ls-pause]');
      this.progress = this.querySelector('[data-ls-progress]');
      this.destination = this.dataset.destination || DESTINATION;
      this.querySelectorAll('[data-ls-shop], [data-ls-link]').forEach((link) => { link.href = this.destination; });
      try { this.slides = normalizeSeed(JSON.parse(this.querySelector('[data-ls-seed]').textContent)); } catch { /* Keep the server-rendered artwork if seed data is unavailable. */ }
      this.artwork = this.slides.find((slide) => slide.artwork);
      const listen = (node, name, handler) => node?.addEventListener(name, handler, { signal: this.controller.signal });
      listen(this.querySelector('[data-ls-prev]'), 'click', () => this.move(-1));
      listen(this.querySelector('[data-ls-next]'), 'click', () => this.move(1));
      listen(this.pauseButton, 'click', () => { this.paused = !this.paused; this.updatePause(); this.schedule(); });
      listen(document, 'visibilitychange', () => { this.updatePause(); this.schedule(); });
      listen(this, 'keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.move(event.key === 'ArrowLeft' ? -1 : 1); }
      });
      listen(this.imageLink, 'pointerdown', (event) => { this.touchStart = { x: event.clientX, y: event.clientY }; this.swiped = false; });
      listen(this.imageLink, 'pointerup', (event) => {
        if (!this.touchStart) return;
        const x = event.clientX - this.touchStart.x;
        const y = event.clientY - this.touchStart.y;
        if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y)) { this.swiped = true; this.move(x < 0 ? 1 : -1); }
        this.touchStart = null;
      });
      listen(this.imageLink, 'click', (event) => { if (this.swiped) { event.preventDefault(); this.swiped = false; } });
      this.updatePause();
      if (this.slides.length) this.render(false);
      this.schedule();
      if (this.dataset.refresh !== 'false') this.refreshCatalog();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.progressFrame);
    }

    updatePause() {
      const stopped = this.paused || document.hidden;
      this.dataset.paused = String(stopped);
      this.pauseButton?.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
      this.pauseButton?.setAttribute('aria-pressed', String(this.paused));
      this.querySelector('[data-ls-pause-icon]')?.toggleAttribute('hidden', this.paused);
      this.querySelector('[data-ls-play-icon]')?.toggleAttribute('hidden', !this.paused);
      this.caption?.setAttribute('aria-live', this.paused ? 'polite' : 'off');
    }

    resetProgress() {
      if (!this.progress) return;
      cancelAnimationFrame(this.progressFrame);
      this.progress.style.animation = 'none';
      void this.progress.offsetWidth;
      this.progressFrame = requestAnimationFrame(() => { if (this.isConnected) this.progress.style.animation = ''; });
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.paused && !document.hidden && this.slides.length > 1) {
        this.resetProgress();
        this.timer = setTimeout(() => this.move(1), INTERVAL);
      }
    }

    move(direction) {
      if (!this.slides.length) return;
      this.index = (this.index + direction + this.slides.length) % this.slides.length;
      this.render();
      this.schedule();
    }

    render(animate = true) {
      const slide = this.slides[this.index];
      if (!slide) return;
      this.dataset.artwork = String(Boolean(slide.artwork));
      this.dataset.index = String(this.index);
      this.dataset.slideCount = String(this.slides.length);
      this.titleNode.textContent = slide.title;
      this.colorNode.textContent = slide.color || 'Liquid Sky NYC';
      this.counter.textContent = `${String(this.index + 1).padStart(3, '0')} / ${String(this.slides.length).padStart(3, '0')}`;
      this.imageLink.setAttribute('aria-label', `${slide.title}${slide.color ? ' — ' + slide.color : ''}. Shop all products`);
      const nextLayer = animate ? 1 - this.activeLayer : this.activeLayer;
      const image = this.images[nextLayer];
      image.alt = `${slide.title}${slide.color ? ' — ' + slide.color : ''}`;
      image.src = slide.image;
      image.classList.add('is-current');
      this.images.forEach((item, layer) => { if (layer !== nextLayer) item.classList.remove('is-current'); });
      this.activeLayer = nextLayer;
      // Warm only the next two assets; the full catalog is never downloaded at once.
      this.preloads = [1, 2].map((offset) => {
        const next = new Image();
        next.src = this.slides[(this.index + offset) % this.slides.length].image;
        return next;
      });
      this.dispatchEvent(new CustomEvent('ls:slide', { detail: { index: this.index, slide, total: this.slides.length } }));
    }

    async refreshCatalog() {
      try {
        const root = this.dataset.catalogRoot || window.Shopify?.routes?.root || '/';
        const base = root.replace(/\/?$/, '/');
        const fetchProducts = async (path) => {
          const products = [];
          const ids = new Set();
          for (let page = 1; ; page++) {
            const response = await fetch(`${base}${path}?limit=250&page=${page}`, { signal: this.controller.signal, credentials: 'same-origin', cache: 'no-cache' });
            if (!response.ok) throw new Error('Catalog request failed');
            const data = await response.json();
            if (!Array.isArray(data.products)) throw new Error('Invalid catalog response');
            const unique = data.products.filter((product) => !ids.has(String(product.id)));
            if (data.products.length && !unique.length) throw new Error('Catalog pagination did not advance');
            unique.forEach((product) => { ids.add(String(product.id)); products.push(product); });
            if (data.products.length < 250) break;
          }
          return products;
        };
        const [products, collection] = await Promise.all([
          fetchProducts('products.json'),
          fetchProducts('collections/all-pg/products.json')
        ]);
        const collectionIds = new Set(collection.map((product) => String(product.id)));
        const ordered = [...collection, ...products.filter((product) => !collectionIds.has(String(product.id)))];
        const catalog = buildSlides(ordered);
        if (!catalog.length) return;
        const current = this.slides[this.index];
        const nextSlides = this.artwork ? [this.artwork, ...catalog] : catalog;
        const retainedIndex = nextSlides.findIndex((slide) => slide.artwork && current?.artwork || (!slide.artwork && !current?.artwork && slide.productId === current?.productId && slide.color === current?.color));
        this.slides = nextSlides;
        this.index = Math.max(0, retainedIndex);
        this.dataset.catalogStatus = 'fresh';
        this.render(false);
        // Keep the current slide's existing three-second deadline.
      } catch (error) {
        if (error.name !== 'AbortError') this.dataset.catalogStatus = 'seed';
      }
    }
  }
  customElements.define('ls-product-carousel', LiquidSkyCarousel);
})();
